import { useParams } from "react-router-dom";
import FullscreenLoader from "../components/FullscreenLoader";
import { useNewReportContext } from "../providers/NewReportProvider"
import { Key, useEffect } from "react";
import parse from "html-react-parser";
import { fileIcon } from "../helpers/utils";
import { ContentType, FileType, FormElementType, FormType, OptionType, ApplicationItemType } from "../helpers/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Chip from "../components/Chip";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { FormElement, FormElementProvider } from "../components/FormElement";
import { isElementVisible } from "../helpers/restrictedVisibility";

const ReportForm = () => {
    const { report, form, section, setReportData } = useNewReportContext();
    const params = useParams();

    useEffect(() => {
        if (report === null) {
            setReportData(params.formType ?? '', parseInt(params.id ?? ''));
        }
    })

    if (!report || !section || !form) {
        return <FullscreenLoader />
    }

    if (section.type === 'form') {
        return <FormGroup />
    }

    return (
        <p>{section.type}</p>
    )
}

const FormGroup = () => {
    const {
        form,
        section,
        formData,
        schema,
        errors,
        updateForm,
        updateErrors,
        application,
    } = useNewReportContext();
    if (!form || !section) return false;
    const formIndex = section.forms.findIndex((s: FormType) => s.url === form.url)
    // Betingelser kan baseres på hvilken søknadsmal søknaden er levert på —
    // gjøres tilgjengelig for isElementVisible via context (søknadens type).
    const visibilityContext = { applicationType: (application as ApplicationItemType | null)?.type };

    return (
        <FormElementProvider
            value={{
                formData,
                schema,
                form,
                section,
                errors,
                updateForm,
                updateErrors,
            }}
        >
            <div className="my-4 my-lg-5">
                <p className="mb-1">{section.title} - {formIndex + 1} / {section.forms.length}</p>
                <h5>{form.title}</h5>
                {form.introtext && parse(form.introtext)}
            </div>

            {form.content && (
                <div className="row mb-5">
                    <Accordion {...form} />
                </div>
            )}

            <div className="row g-4">
                {form.elements.map((item: FormElementType, index: Key) => {
                    // Begrenset visning samme som søknader — fjernede felter
                    // skjules basert på dagens formData.
                    if (
                        item.type !== 'hidden' &&
                        !isElementVisible(item.restrictedVisibility, formData, visibilityContext)
                    ) {
                        return null;
                    }
                    return (
                        <div className={item.display ?? "col-12"} key={index}>
                            <FormElement {...item} />
                        </div>
                    );
                })}
            </div>
        </FormElementProvider>
    )
}

type ResolvedContentRef = {
    name: string;
    label: string;
    type: string;
    /** Valgalternativene fra søknadsmalen — nødvendige for å vise etiketter
     *  framfor lagrede verdier på dropdown/dropdowncheck/RadioGroup. */
    options?: OptionType[];
};

/**
 * Mapper form-builder-elementtyper til render-typene ContentElement støtter.
 *
 * `option` er for felter der den lagrede verdien er en NØKKEL som må slås opp
 * i `options`/`alternatives` for å bli lesbar. Uten den rendret RadioGroup og
 * bigradio den rå verdien («ja_1») i stedet for etiketten.
 */
const mapElementTypeToContentType = (elType: string): string => {
    switch (elType) {
        case 'numberformat':
            return 'number';
        case 'fileupload':
        case 'fileuploadsingle':
            return 'file';
        case 'dropdown':
        case 'dropdowncheck':
            return 'chip';
        case 'RadioGroup':
        case 'bigradio':
        case 'radiogroupdepending':
            return 'option';
        case 'checkbox':
        case 'ToggleSwitch':
            return 'boolean';
        case 'date':
            return 'date';
        case 'account':
            return 'account';
        case 'budgetlines':
        case 'expensetable':
        case 'repeater':
            return 'rows';
        default:
            return 'text';
    }
};

/**
 * Slår opp felt-meta i en søknadsmal.
 *
 * Går rekursivt: `depends[].innerElements` for ALLE typer som har dem (ikke
 * bare `radiogroupdepending`), og `element.elements` for typer som nøster
 * direkte. Uten det ble et felt inne i en repeater ikke funnet — og siden
 * lyfti-api nå faktisk sender `applicationTemplate`, ville et uresolvert felt
 * blitt SKJULT i stedet for vist som tekst.
 */
const lookupFieldInTemplate = (
    fieldName: string,
    template: Record<string, any>
): ResolvedContentRef | null => {
    const resolve = (el: any): ResolvedContentRef => ({
        name: el.name,
        label: el.label || el.name,
        type: mapElementTypeToContentType(el.type ?? 'text'),
        // dropdown/dropdowncheck bruker `options`; RadioGroup/bigradio
        // bruker `alternatives`. Begge er {label, value}.
        options: Array.isArray(el.options)
            ? el.options
            : Array.isArray(el.alternatives)
                ? el.alternatives
                : undefined,
    });

    const walk = (elements: any): ResolvedContentRef | null => {
        if (!Array.isArray(elements)) return null;

        for (const el of elements) {
            if (!el || typeof el !== 'object') continue;

            if (el.name === fieldName) return resolve(el);

            for (const dep of (el.depends ?? [])) {
                const hit = walk(dep?.innerElements);
                if (hit) return hit;
            }

            const nested = walk(el.elements);
            if (nested) return nested;
        }

        return null;
    };

    const sections: any[] = Array.isArray(template?.sections) ? template.sections : [];
    for (const section of sections) {
        for (const form of (Array.isArray(section?.forms) ? section.forms : [])) {
            const hit = walk(form?.elements);
            if (hit) return hit;
        }
    }

    return null;
};

/**
 * Etiketten for én lagret multivalg-/radioverdi.
 *
 * Multivalg er lagret på TRE former i produksjon, og alle tre finnes:
 *   - skalarer            `["fng_03"]`                      ← det søkerportalen skriver i dag
 *   - objekter            `[{label, value}]`                ← eldre innsendinger
 *   - dobbeltkodet JSON   `"[{\"label\":…}]"`
 *
 * `ContentElement` spredte tidligere elementet rett inn i `<Chip {...item}>`.
 * For skalarformen ga det `label: undefined` — altså en TOM pille. Alle
 * FN-bærekraftsmål i «Innhold fra søknaden» var blanke for søknader levert
 * etter at søkerportalen gikk over til skalarer.
 *
 * Samme oppslag som `Accordion.tsx` gjør for søknadsoppsummeringen.
 */
const optionLabel = (item: any, options?: OptionType[]): string => {
    const fromOptions = options?.find((o) => o.value === item)?.label;
    if (fromOptions) return fromOptions;

    if (item && typeof item === 'object') {
        return item.label ?? item.value ?? '';
    }

    return String(item ?? '');
};

/** Multivalg-verdi normalisert til en liste, uansett lagret form. */
const toValueList = (value: any): any[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return [value];
        }
    }
    if (value === null || value === undefined || value === '') return [];
    return [value];
};

const Accordion = (props: FormType) => {
    const { content } = props;
    const { t } = useTranslation();
    const { application } = useNewReportContext();

    if (!content || !application) return false;

    const app = application as ApplicationItemType;
    // Den eksakte (versjonerte) søknadsmalen søknaden ble fylt ut med — resolved
    // server-side. Render-type og label slås opp herfra, så vi alltid speiler
    // den layouten søkeren faktisk så da søknaden ble levert.
    const tplPayload = app.applicationTemplate ?? null;

    // Resolve hver content-item til {name, label, type} eller null (hopp over).
    // Felter er IKKE lenger bundet til en bestemt søknadsmal/type — et felt
    // vises kun hvis `name` faktisk finnes i søknadsmalen søknaden ble levert
    // på (`applicationTemplate`). Render-type + label hentes derfra.
    // Regler per shape:
    // - `{value,template}` (template-ID-bundet, legacy): krever match på
    //   `application.template`, slår opp i malen.
    // - `{label,name,type}` (legacy auto-generert): vis som-er hvis feltet ikke
    //   finnes i malen (bakoverkompabilitet).
    // - `{label,name}` (nytt) og `{label,name,template_type}` (eldre — template_type
    //   ignoreres): navn-basert. Vis KUN hvis feltet finnes i malen.
    const resolved: ResolvedContentRef[] = content
        .map((item): ResolvedContentRef | null => {
            if ('value' in item) {
                if (!app.template || Number(app.template) !== Number(item.template)) {
                    return null;
                }
                const fromTpl = tplPayload ? lookupFieldInTemplate(item.value, tplPayload) : null;
                return fromTpl ?? { name: item.value, label: item.value, type: 'text' };
            }

            // Merk: lyfti-api sender `applicationTemplate` på søkerveien siden
            // 2026-08-27. Før det var `tplPayload` alltid null, så ALT havnet i
            // fallbacken under og rendret som `text` — filer, multivalg og tall
            // inkludert. Nå resolver felt mot malen, og et felt som IKKE finnes
            // i den blir skjult. Derfor må lookupFieldInTemplate være
            // uttømmende (den rekurserer nå gjennom depends og elements).
            const fromTpl = tplPayload ? lookupFieldInTemplate(item.name, tplPayload) : null;
            if (fromTpl) return fromTpl;
            // legacy {label,name,type}: behold visning som-er
            if ('type' in item && typeof item.type === 'string') {
                return { name: item.name, label: item.label, type: item.type };
            }
            // Ingen søknadsmal tilgjengelig → kan ikke verifisere; vis lagret
            // label som tekst i stedet for å skjule alt innhold.
            if (!tplPayload) {
                return { name: item.name, label: item.label, type: 'text' };
            }
            // Malen finnes, men feltet er ikke i den → skjul.
            return null;
        })
        .filter((x): x is ResolvedContentRef => x !== null);

    if (resolved.length === 0) return false;

    return (
        <div className="accordion application-info-accordeon" id="summaryAccordion">
            <div className="accordion-item">
                <h2 className="accordion-header">
                    <button className={`accordion-button collapsed`} type="button" data-bs-toggle="collapse" data-bs-target={`#collapse`} aria-expanded="true" aria-controls={`collapse`}>
                        {t('report_content_from_application')}
                    </button>
                </h2>
                <div id={`collapse`} className={`accordion-collapse collapse`} data-bs-parent="#summaryAccordion">
                    <div className="accordion-body">
                        <div className="row gy-3">
                            {resolved.map((item, index) => (
                                <div className="col-12" key={index}>
                                    <ContentElement {...item} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const ContentElement = (props: ResolvedContentRef) => {
    const { name, label, type, options } = props;
    const { application } = useNewReportContext();
    const { t } = useTranslation();

    if (!application) return false;

    const value = application['payload'][name];

    switch (type) {
        case 'text':
            return (
                <>
                    <label className="fw-semibold">{label}</label>
                    <p className="mb-0">{value}</p>
                </>
            );
        case 'file':
            let file: FileType;
            if (Array.isArray(application['attachments'][name])) {
                file = application['attachments'][name][0];
            } else {
                file = application['attachments'][name];
            }

            if (!file) return null;

            return (
                <>
                    <label className="fw-semibold mb-1">{label}</label>
                    <p className="mb-0">
                        <a href={`${import.meta.env.VITE_API_BASE_URL}${file.download_url}`} className="text-decoration-none">
                            <FontAwesomeIcon icon={fileIcon(file.name?.split('.').pop() ?? '')} className="me-2" />
                            {file.name}
                        </a>
                    </p>
                </>
            );
        case 'chip': {
            // JSON.parse ble tidligere kalt rått på en ikke-array — det kastet
            // på en skalar streng. toValueList tåler alle tre lagrede former.
            const items = toValueList(value);
            if (items.length === 0) return null;

            return (
                <>
                    <label className="fw-semibold mb-1">{label}</label>
                    <div className="mb-0">
                        {items.map((item: any, index: number) => (
                            <Chip key={index} label={optionLabel(item, options)} type="subtle" />
                        ))}
                    </div>
                </>
            );
        }
        case 'option': {
            // Enkeltvalg: den lagrede verdien er en nøkkel, ikke etiketten.
            if (value === null || value === undefined || value === '') return null;

            return (
                <>
                    <label className="fw-semibold">{label}</label>
                    <p className="mb-0">{optionLabel(value, options)}</p>
                </>
            );
        }
        case 'boolean':
            return (
                <>
                    <label className="fw-semibold">{label}</label>
                    <p className="mb-0">
                        {value === true || value === 1 || value === '1' || value === 'true'
                            ? t('yes')
                            : t('no')}
                    </p>
                </>
            );
        case 'date': {
            if (!value) return null;
            const parsed = new Date(value);
            return (
                <>
                    <label className="fw-semibold">{label}</label>
                    <p className="mb-0">
                        {isNaN(parsed.getTime())
                            ? String(value)
                            : parsed.toLocaleDateString('nb-NO')}
                    </p>
                </>
            );
        }
        case 'account': {
            if (!value) return null;
            const digits = String(value).replace(/\D/g, '');
            return (
                <>
                    <label className="fw-semibold">{label}</label>
                    <p className="mb-0">
                        {digits.length === 11
                            ? `${digits.slice(0, 4)} ${digits.slice(4, 6)} ${digits.slice(6)}`
                            : String(value)}
                    </p>
                </>
            );
        }
        case 'rows': {
            // Kostnadsbudsjett, utleggstabell og repeterende grupper lagrer en
            // liste av rader. Disse rendret tidligere som `text`, altså rått
            // objekt-innhold.
            const rows = Array.isArray(value) ? value : [];
            if (rows.length === 0) return null;

            const columns = Object.keys(
                rows.reduce((acc: Record<string, true>, row: any) => {
                    if (row && typeof row === 'object') {
                        Object.keys(row).forEach((k) => { acc[k] = true; });
                    }
                    return acc;
                }, {})
            );

            return (
                <>
                    <label className="fw-semibold mb-1">{label}</label>
                    <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <tbody>
                                {rows.map((row: any, rowIndex: number) => (
                                    <tr key={rowIndex}>
                                        {columns.map((col) => {
                                            const cell = row?.[col];
                                            return (
                                                <td key={col}>
                                                    {cell && typeof cell === 'object'
                                                        ? (cell.name ?? '')
                                                        : String(cell ?? '')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            );
        }
        case 'number':
            return (
                <>
                    <label className="fw-semibold mb-1">{label}</label>
                    <div>
                        <NumericFormat
                            value={value}
                            displayType="text"
                            thousandSeparator=" "
                        />
                    </div>

                </>
            )
    }

    // Fall-through returnerte tidligere `name` — altså det rå feltnavnet, som
    // ser ut som innhold. Vis verdien om den er vist-bar, ellers ingenting.
    if (value === null || value === undefined || value === '' || typeof value === 'object') {
        return null;
    }

    return (
        <>
            <label className="fw-semibold">{label}</label>
            <p className="mb-0">{String(value)}</p>
        </>
    );
}

export default ReportForm;
